import { useSnackbar } from '@/features/snackbar';

import { useEffect, useRef, useState } from 'react';
import {
  HumanReadableError,
  ImageUploadResponseSchema,
  RTE_FILE_UPLOAD_EVENT_NAME,
  uploadFile,
  validateFile,
} from './utils';
import { useTracking } from 'react-tracking';

const ERROR_SNACKBAR_ID = 'file-upload-failed-snackbar';

export const useFileUpload = () => {
  const [progress, setProgress] = useState<null | number>(null);
  const [error, setError] = useState<null | string>(null);
  const urlRef = useRef<string | null>(null);

  const abortRef = useRef<(() => void) | null>(null);
  const { addSnackbar, removeSnackbar } = useSnackbar();
  const { trackEvent } = useTracking({
    type: 'track',
    subject: 'file-upload',
  });

  const handleFileUpload = async (file: File | undefined | null) => {
    setError(null);
    removeSnackbar({ id: ERROR_SNACKBAR_ID });
    if (!file) return null;
    trackEvent({
      action: 'initiated',
      fileType: file.type,
      fileSize: file.size,
    });
    try {
      validateFile(file);

      setProgress(0);

      const response = await uploadFile(file, {
        onProgress: setProgress,
        onUploadInitiated: ({ abort }) => {
          abortRef.current = abort;
        },
      });

      // Aborted request handle silently
      if (!response) {
        trackEvent({
          action: 'aborted',
        });
        return null;
      }

      if (!response.ok) {
        throw new Error('Server did not accept the file');
      }

      const data = await response.json();
      const parsedData = ImageUploadResponseSchema.safeParse(data).data;
      const firstUrl = parsedData?.[0] ?? null;

      if (!firstUrl) throw new Error("Server didn't return a valid URL");

      urlRef.current = firstUrl;

      trackEvent({
        action: 'uploaded',
      });
      return firstUrl;
    } catch (error) {
      console.error('Failed to upload file', error);
      const errorMessage =
        error instanceof HumanReadableError && error.message
          ? error.message
          : 'Failed to upload the file';

      setError(errorMessage);

      addSnackbar({
        id: ERROR_SNACKBAR_ID,
        type: 'negative',
        message: errorMessage,
        lifeTime: 30000,
        actionLabel: 'OK',
      });
      trackEvent({
        action: 'failed',
      });
      return null;
    } finally {
      setProgress(null);
    }
  };

  useEffect(() => {
    const abortFileUpload = () => {
      setProgress(null);
      setError(null);
      urlRef.current = null;
      if (typeof abortRef.current === 'function') abortRef.current();
      abortRef.current = null;
    };

    document.addEventListener(RTE_FILE_UPLOAD_EVENT_NAME, abortFileUpload);

    return () => {
      document.removeEventListener(RTE_FILE_UPLOAD_EVENT_NAME, abortFileUpload);
    };
  });

  useEffect(() => {
    const abortFileUpload = () => {
      setProgress(null);
      setError(null);
      urlRef.current = null;
      if (typeof abortRef.current === 'function') abortRef.current();
      abortRef.current = null;
    };

    document.addEventListener(RTE_FILE_UPLOAD_EVENT_NAME, abortFileUpload);

    return () => {
      document.removeEventListener(RTE_FILE_UPLOAD_EVENT_NAME, abortFileUpload);
    };
  });

  return {
    handleFileUpload,
    progress,
    urlRef,
    error,
  };
};
